import React, { useState, FC, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  CircularProgress,
  Avatar,
  Skeleton,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import { useGetOnboardingStatusQuery } from './healthWalletSlice';
import { OnboardingFlow } from './OnboardingFlow';
//import { VerificationWrapper } from './VerificationWrapper';
//import { isOnboardingComplete, clearAllClearAuth } from './clearTokenManager';
import { BRAND_DARKER, BG } from './healthWalletConstants';
import { HealthWalletRecords } from './HealthWalletRecords';
import { HwUnavailable } from './HwUnavailable';
import { useAuthProfile } from "../../app/hooks";
import { AppLayout, AuthLayout } from '../../App';
import { UnAuthorizedMessage } from '../messaging/UnAuthorizedMessage';
import { SkeletonWithRetry } from "../../app/components/ProfileFetchError";
import { getCurrentOnboardingStepFromMeta } from "./onboardingUtils";
import { printError } from "../../app/utils";

// --- Main Component ---

export const HealthWalletMain: FC = () => {
  const { authStatus, isLoading: isLoadingLoggedInUserProfile, isSuccess: isSuccessLoggedInUserProfile, isError: isErrorLoggedInUserProfile, error: errorLoggedInUserProfile } = useAuthProfile();
  const [statusError, setStatusError] = useState<string | null>(null);
 // const [showOnboarding, setShowOnboarding] = useState<boolean | null>(null);
  const [needsReAuth, setNeedsReAuth] = useState(false);

  const isAuthenticated = !isLoadingLoggedInUserProfile && isSuccessLoggedInUserProfile && authStatus === 'authenticated';

  // // Platform sign-out → automatically sign out of health wallet too
  // useEffect(() => {
  //   if (authStatus === 'unauthenticated') {
  //     //clearAllClearAuth();
  //     setShowOnboarding(null);
  //     setNeedsReAuth(false);
  //   }
  // }, [authStatus]);

  // Query onboarding status from backend (only when authenticated)
    const onboardingStatusQueryMeta = useGetOnboardingStatusQuery(undefined, { skip: !isAuthenticated });
    const { data: onboardingStatus, isLoading: isOnboardingLoading, isError: isOnboardingError, error: onboardingError } = onboardingStatusQueryMeta;


  // Determine onboarding state and CLEAR token validity
  useEffect(() => {
    if (isOnboardingLoading || !isAuthenticated) return;
    // If onboarding is already active, let it finish via handleOnboardingComplete
    // if (showOnboarding === true) return;

    let onboarded = false;
    if (isOnboardingError) {
      // Backend error: set error state to pass to onboarding component
      console.error('Onboarding status query error:', onboardingError);
      setStatusError(printError(onboardingError) || 'Failed to load onboarding status. Please try again later.');
      // Continue with onboarding flow to show error within the UI
      // setShowOnboarding(true);
      setNeedsReAuth(false);
      return;
    } else if (onboardingStatus) {
      onboarded = onboardingStatus.isOnboarded;
      setStatusError(null); // Clear any previous errors
    }

    if (!onboarded) {
      // New user — needs onboarding
      // setShowOnboarding(true);
      setNeedsReAuth(false);
    } else if (onboardingStatus?.verificationStatus !== 'verified') {
      // Onboarded but identity verification not completed/expired — needs re-auth
      // setShowOnboarding(false);
      setNeedsReAuth(true);
    } else {
      // Onboarded and identity verified — go to wallet
      // setShowOnboarding(false);
      setNeedsReAuth(false);
    }
  }, [onboardingStatus, isOnboardingLoading, isOnboardingError, isAuthenticated]);

  const handleOnboardingComplete = useCallback(() => {
    //setShowOnboarding(false);
    setNeedsReAuth(false);
  }, []);

  const handleReturningUser = useCallback(() => {
    // Returning user clicked "I Already Have a Wallet" — mark as onboarded locally and skip
    // isOnboardingComplete();
    // setShowOnboarding(false);
  }, []);

  const handleReAuthComplete = useCallback(() => {
    setNeedsReAuth(false);
  }, []);

  // Authentication checks
  if (isLoadingLoggedInUserProfile) {
    return (
      <AppLayout>
        <SkeletonWithRetry resourceLabel="Health Wallet" />
      </AppLayout>
    );
  }

  if (authStatus === 'unauthenticated') {
    return (
      <AuthLayout>
        <UnAuthorizedMessage>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
            <MonitorHeartIcon sx={{ color: BRAND_DARKER, fontSize: 28 }} />
            <Typography variant="subtitle2" sx={{ color: BRAND_DARKER, fontWeight: 600 }}>
              Sign in to access your Health Wallet
            </Typography>
          </Box>
        </UnAuthorizedMessage>
      </AuthLayout>
    );
  }

  if (isErrorLoggedInUserProfile) {
    console.error("LoggedInUserProfile Error", errorLoggedInUserProfile);
    return <AppLayout><Skeleton variant="rectangular" width="100%" height={400} /></AppLayout>;
  }

  // RTK transport-level failure talking to map server (no response body at all).
  // Render the unavailable screen with a fixed-string accordion detail since we
  // don't have a server-supplied message.
  if (statusError) {
    return (
      <AppLayout>
        <HwUnavailable errorDetail={statusError} />
      </AppLayout>
    );
  }

  // Server reached but signalled failure via the `error` field — admin downstream
  // unavailable. Accordion shows the server-supplied error message.
  if (onboardingStatus?.error) {
    return (
      <AppLayout>
        <HwUnavailable errorDetail={onboardingStatus.error} />
      </AppLayout>
    );
  }

  // Determine whether to show onboarding or records.
  // While clearVerifiedInFlow is set, hold OnboardingFlow mounted so the user has
  // time to acknowledge the success/error screen — without this gate, the moment
  // verificationStatus flips to 'verified', records would render and the result
  // screen would flash and disappear.
  const shouldShowOnboarding =
    !!onboardingStatus?.clearVerifiedInFlow ||
    getCurrentOnboardingStepFromMeta(onboardingStatusQueryMeta) !== 'data';

  if ( shouldShowOnboarding) {
    return (
      <OnboardingFlow
        onComplete={handleOnboardingComplete}
        onReturningUser={handleReturningUser}
      />
    );
  }

  // Show records component
  return <HealthWalletRecords />;
};
